import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  HostListener,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgIf, NgFor, NgClass, DatePipe } from '@angular/common';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { TooltipDirective } from '../ni-tooltip/ni-tooltip.directive';
import { NiIconComponent } from '../ni-icon/ni-icon.component';
import { generateSecureRandomString } from '../../../../shared/utils/random-utils';
import { DateUtils } from '../../../../shared/utils/date-utils';

interface RippleState {
  show: boolean;
  x: number;
  y: number;
  target?: string;
}

type TimeField = 'hour' | 'minute' | 'second';

@Component({
  selector: 'NiFormDatePicker',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, TooltipDirective, NiIconComponent],
  animations: [
    trigger('calendarAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate(
          '200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ opacity: 0, transform: 'translateY(10px)' })
        ),
      ]),
    ]),
    trigger('monthAnimation', [
      state('void', style({ opacity: 0, transform: 'translateY(30px)' })),
      state('*', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('* => void', animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('void => *', animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.9)' }),
        animate(
          '100ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ opacity: 1, transform: 'scale(1)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '100ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ opacity: 0, transform: 'scale(0.9)' })
        ),
      ]),
    ]),
    trigger('rippleAnimation', [
      transition(':enter', [
        style({ transform: 'scale(0)', opacity: 0.5 }),
        animate(
          '400ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ transform: 'scale(2)', opacity: 0 })
        ),
      ]),
    ]),
    trigger('skeletonFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
    trigger('inputFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ],
  templateUrl: './ni-date-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NiDatePickerComponent),
      multi: true,
    },
    DatePipe,
  ],
})
export class NiDatePickerComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() id: string = `datepicker-${generateSecureRandomString(7)}`;
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() hasError: boolean = false;
  @Input() errorMessage: string = '';
  @Input() enableTime: boolean = false;
  @Input() enableSeconds: boolean = false;
  @Input() use24Hour: boolean = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() disabledDates?: Date[];
  @Input() firstDayOfWeek: 0 | 1 = 0;
  @Input() format: string = 'MM/dd/yyyy';
  @Input() timeFormat: string = 'h:mm a';
  @Input() isLoading: boolean = false;
  @Input() placeholder: string = 'MM/DD/YYYY';
  @Input() theme: 'light' | 'dark' = 'light';
  @Input() position: 'auto' | 'above' | 'below' = 'auto';
  @Input() inline: boolean = false;
  @Input() showWeekNumbers: boolean = false;
  private _allowInput = false;
  @Input() set allowInput(value: boolean | string | null | undefined) {
    this._allowInput = value === true || value === 'true';
  }
  get allowInput(): boolean {
    return this._allowInput;
  }
  @Input() clearable: boolean = true;
  @Input() closeOnSelect: boolean = true;
  @Input() disabled: boolean = false;
  @Input() yearRange: number = 100;
  @Input() rangeMode: boolean = false;
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() date: Date | null = null;
  @Input() isPopover : boolean = false
  
  @Output() dateChange = new EventEmitter<Date | null>();
  @Output() rangeChange = new EventEmitter<{ start: Date | null; end: Date | null }>();
  @ViewChild('datePickerContainer') datePickerContainer!: ElementRef;
  @ViewChild('inputElement') inputElement!: ElementRef;
  @ViewChild('yearDropdownRef') yearDropdownRef!: ElementRef;

  selectedDate: Date | null = null;
  currentMonth: number = 0;
  currentYear: number = 0;
  isOpen: boolean = false;
  calendarDays: Date[][] = [];
  inputValue: string = '';
  private isInputFocused = false;
  monthDropdownOpen: boolean = false;
  yearDropdownOpen: boolean = false;
  currentMonthViewState: string = 'initial';
  hoursInputId: string = `datepicker-hours-${generateSecureRandomString(7)}`;
  minutesInputId: string = `datepicker-minutes-${generateSecureRandomString(7)}`;
  rippleState: RippleState = {
    show: false,
    x: 0,
    y: 0,
    target: '',
  };
  calendarPosition: 'above' | 'below' = 'below';
  private isInitialLoad: boolean = true; // Track if this is the initial load
  private initialValue: Date | null = null; // Store initial value without selecting it

  months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  weekdayLabels: string[] = [];
  yearOptions: number[] = [];

  // ControlValueAccessor requirements
  private onChange: any = () => {};
  private onTouched: any = () => {};

  private selectionStep: 'start' | 'end' = 'start';

  constructor(private readonly datePipe: DatePipe) {
    // Set default calendar view to today's date instead of 1990
    const today = new Date();
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
  }

  ngOnInit(): void {
    // Generate weekday labels based on first day of week
    this.generateWeekdayLabels();

    // Generate calendar days
    this.generateCalendarDays();

    // Mark initial load as complete after a short delay
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 100);

    // Generate year options
    this.generateYearOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['date']) {
      this.handleDateChange(changes['date'].currentValue);
      return;
    }

    if (changes['startDate'] || changes['endDate']) {
      this.updateInputValue();
    }
  }

  private handleDateChange(dateValue: any): void {
    const date = this.parseDateValue(dateValue);

    if (date) {
      this.applyValidDate(date);
    } else {
      this.applyInvalidDate();
    }
  }

  private parseDateValue(value: any): Date | null {
    return DateUtils.coerceToLocalDate(value);
  }

  private applyValidDate(date: Date): void {
    this.selectedDate = date;
    this.currentMonth = date.getMonth();
    this.currentYear = date.getFullYear();
    this.generateCalendarDays();
    this.updateInputValue();

    if (this.isInitialLoad) {
      this.initialValue = date;
    }
  }

  private applyInvalidDate(): void {
    if (this.isInitialLoad) {
      this.initialValue = null;
    }

    this.selectedDate = null;
    this.inputValue = '';
    this.setCalendarViewToDefault();
  }


  // Determine the best position for the calendar
  determineCalendarPosition(): void {
    if (this.position !== 'auto') {
      this.calendarPosition = this.position;
      return;
    }

    if (!this.datePickerContainer) return;

    // If there's not enough space below but enough space above
    // if (spaceBelow < calendarHeight && rect.top > calendarHeight) {
    //   this.calendarPosition = 'above';
    // } else {
    //   this.calendarPosition = 'below';
    // }
    this.calendarPosition = 'below';
  }

  // Start ripple animation
  startRipple(event?: Event, target: string = ''): void {
    if (event) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const x = (event as MouseEvent).clientX - rect.left;
      const y = (event as MouseEvent).clientY - rect.top;

      this.rippleState = {
        show: true,
        x,
        y,
        target,
      };
    } else {
      this.rippleState = {
        show: true,
        x: 0,
        y: 0,
        target,
      };
    }

    setTimeout(() => {
      if (this.rippleState.target === target) {
        this.rippleState.show = false;
      }
    }, 400);
  }

  // Toggle dropdowns
  toggleMonthDropdown(event: Event): void {
    event.stopPropagation();
    this.monthDropdownOpen = !this.monthDropdownOpen;
    this.yearDropdownOpen = false; // Close other dropdown
    this.startRipple();
  }

  toggleYearDropdown(event: Event): void {
    event.stopPropagation();
    this.yearDropdownOpen = !this.yearDropdownOpen;
    this.monthDropdownOpen = false; // Close other dropdown
    this.startRipple();

    if (this.yearDropdownOpen) {
      setTimeout(() => this.scrollToSelectedYear(), 0);
    }
  }

  private scrollToSelectedYear(): void {
    if (!this.yearDropdownRef) return;

    const dropdown = this.yearDropdownRef.nativeElement;
    const selectedButton = dropdown.querySelector(`button[class*="bg-primary"]`);

    if (selectedButton) {
      selectedButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Update month selection method
  selectMonth(month: number, event: Event): void {
    event.stopPropagation();

    // Trigger animation on month change
    this.currentMonthViewState = 'void';
    setTimeout(() => {
      this.currentMonth = month;
      this.monthDropdownOpen = false;
      this.generateCalendarDays();
      this.currentMonthViewState = 'initial';
    }, 150);

    this.startRipple();
  }

  // Update year selection method
  selectYear(year: number, event: Event): void {
    event.stopPropagation();

    // Trigger animation on year change
    this.currentMonthViewState = 'void';
    setTimeout(() => {
      this.currentYear = year;
      this.yearDropdownOpen = false;
      this.generateCalendarDays();
      this.currentMonthViewState = 'initial';
    }, 150);

    this.startRipple();
  }

  // Generate year options for the dropdown
  generateYearOptions(): void {
    const currentYear = new Date().getFullYear();
    const maxyear = this.maxDate ? this.maxDate.getFullYear() : currentYear + this.yearRange;
    this.yearOptions = [];

    for (let i = currentYear - this.yearRange; i <= maxyear; i++) {
      this.yearOptions.push(i);
    }
  }

  // Generate weekday labels (Sun-Sat or Mon-Sun)
  generateWeekdayLabels(): void {
    const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    this.weekdayLabels = [
      ...weekdays.slice(this.firstDayOfWeek),
      ...weekdays.slice(0, this.firstDayOfWeek),
    ];
  }

  // Generate calendar days
  generateCalendarDays(): void {
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);

    // Determine the first day to show in the calendar
    const firstDayToShow = new Date(firstDayOfMonth);
    let dayOfWeek = firstDayOfMonth.getDay();

    // Adjust for first day of week
    if (dayOfWeek < this.firstDayOfWeek) {
      dayOfWeek += 7;
    }

    firstDayToShow.setDate(firstDayToShow.getDate() - (dayOfWeek - this.firstDayOfWeek));

    // Create 6 weeks (42 days) to ensure enough space
    this.calendarDays = [];
    let currentWeek: Date[] = [];

    for (let i = 0; i < 42; i++) {
      const day = new Date(firstDayToShow);
      day.setDate(firstDayToShow.getDate() + i);

      currentWeek.push(day);

      if (currentWeek.length === 7) {
        this.calendarDays.push(currentWeek);
        currentWeek = [];
      }
    }
  }

  // Update the input value based on selected date and format
  updateInputValue(): void {
    if (this.allowInput && this.isInputFocused && !this.selectedDate) {
      return;
    }

    if (this.rangeMode) {
      let formatted = '';

      if (this.startDate) {
        formatted = this.formatDateTime(this.startDate);

        if (this.endDate) {
          formatted += ' - ' + this.formatDateTime(this.endDate);
        } else if (this.selectionStep === 'end') {
          formatted += ' - ...';
        }
      }

      this.inputValue = formatted;
    } else if (!this.selectedDate) {
      this.inputValue = '';
    } else {
      this.ensureSelectedDateIsDate();
      this.inputValue =
        this.selectedDate instanceof Date ? this.formatDateTime(this.selectedDate) : '';
    }

    this.applyInputDisplayValue();
  }

  private applyInputDisplayValue(): void {
    const el = this.inputElement?.nativeElement as HTMLInputElement | undefined;
    if (!el) {
      return;
    }
    if (el.value !== this.inputValue) {
      el.value = this.inputValue;
    }
  }

  // Toggle the calendar open/closed
  toggleCalendar(): void {
    if (this.disabled) return;

    if (!this.isOpen) {
      // When opening with no selection, show the default view (e.g. maxDate for DOB age restriction)
      if (!this.selectedDate && !this.rangeMode) {
        this.setCalendarViewToDefault();
      }
      // Determine position before opening
      this.determineCalendarPosition();
      this.isOpen = true;
      this.currentMonthViewState = 'initial';
    } else {
      this.isOpen = false;
    }
  }

  /**
   * Set calendar view (month/year) to a sensible default when there's no selected date.
   * If maxDate is set and is in the past (e.g. 17 years ago for DOB), show that so
   * users see selectable dates immediately instead of having to navigate back.
   */
  private setCalendarViewToDefault(): void {
    const today = new Date();
    if (this.maxDate) {
      const max = new Date(this.maxDate);
      max.setHours(0, 0, 0, 0);
      const todayStart = new Date(today);
      todayStart.setHours(0, 0, 0, 0);
      if (max <= todayStart) {
        this.currentMonth = max.getMonth();
        this.currentYear = max.getFullYear();
        this.generateCalendarDays();
        this.generateYearOptions();
        return;
      }
    }
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.generateCalendarDays();
  }

  // Select a date
  selectDate(date: Date): void {
    if (this.isDisabled(date) || !this.isInCurrentMonth(date)) return;

    let newDate = new Date(date);

    // Preserve time if already selected
    if (this.selectedDate && this.enableTime) {
      this.ensureSelectedDateIsDate();
      if (this.selectedDate instanceof Date) {
        newDate.setHours(
          this.selectedDate.getHours(),
          this.selectedDate.getMinutes(),
          this.enableSeconds ? this.selectedDate.getSeconds() : 0
        );
      }
    } else {
      // Set default time
      newDate.setHours(0, 0, 0, 0);
    }

    this.selectedDate = newDate;
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);

    // Close the calendar if time picking is not enabled or closeOnSelect is true
    if ((!this.enableTime || this.closeOnSelect) && !this.inline) {
      setTimeout(() => {
        this.isOpen = false;
      }, 200); // Short delay for animation
    }

    this.startRipple(undefined, 'day-' + date.getTime());
  }

  // Clear the selected date
  clearDate(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.isInputFocused = false;
    this.isOpen = false;
    this.selectedDate = null;
    this.inputValue = '';
    this.applyInputDisplayValue();
    this.onChange(null);
    this.onTouched();
    this.dateChange.emit(null);
    this.startRipple();
  }

  hasClearableValue(): boolean {
    if (this.rangeMode) {
      return !!(this.startDate || this.endDate);
    }
    return !!(this.selectedDate || (this.allowInput && this.inputValue?.trim()));
  }

  // Go to today
  goToToday(): void {
    const today = new Date();
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.generateCalendarDays();

    if (!this.rangeMode) {
      // For single date mode
      this.selectedDate = new Date(today);
      this.onChange(this.selectedDate);
      this.dateChange.emit(this.selectedDate);
    }

    this.updateInputValue();
  }

  // Navigate to previous month
  prevMonth(): void {
    this.currentMonthViewState = 'void';

    setTimeout(() => {
      this.currentMonth--;
      if (this.currentMonth < 0) {
        this.currentMonth = 11;
        this.currentYear--;
      }
      this.generateCalendarDays();
      this.currentMonthViewState = 'initial';
    }, 150);
  }

  // Navigate to next month
  nextMonth(): void {
    this.currentMonthViewState = 'void';

    setTimeout(() => {
      this.currentMonth++;
      if (this.currentMonth > 11) {
        this.currentMonth = 0;
        this.currentYear++;
      }
      this.generateCalendarDays();
      this.currentMonthViewState = 'initial';
    }, 150);
  }

  // Check if a date is today
  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  /**
   * Ensure selectedDate is a valid Date object
   */
  private ensureSelectedDateIsDate(): void {
    if (this.selectedDate && !(this.selectedDate instanceof Date)) {
      // Try to convert if it's not a Date
      const convertedDate = new Date(this.selectedDate as any);
      if (!isNaN(convertedDate.getTime())) {
        this.selectedDate = convertedDate;
      } else {
        this.selectedDate = null;
      }
    }
  }

  // Check if a date is selected
  isSelected(date: Date): boolean {
    if (!this.selectedDate) return false;

    // Ensure selectedDate is a Date object
    this.ensureSelectedDateIsDate();
    if (!this.selectedDate) return false;

    return (
      date.getDate() === this.selectedDate.getDate() &&
      date.getMonth() === this.selectedDate.getMonth() &&
      date.getFullYear() === this.selectedDate.getFullYear()
    );
  }

  // Check if a date is in the current month
  isInCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.currentMonth;
  }

  // Check if a date is disabled
  isDisabled(date: Date): boolean {
    // Check min date
    if (this.minDate) {
      const minDate = new Date(this.minDate);
      minDate.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      if (checkDate < minDate) {
        return true;
      }
    }

    // Check max date
    if (this.maxDate) {
      const maxDate = new Date(this.maxDate);
      maxDate.setHours(23, 59, 59, 999);
      const checkDate = new Date(date);
      checkDate.setHours(23, 59, 59, 999);
      if (checkDate > maxDate) {
        return true;
      }
    }

    // Check disabled dates
    if (this.disabledDates) {
      return this.disabledDates.some(
        (disabledDate) =>
          date.getDate() === disabledDate.getDate() &&
          date.getMonth() === disabledDate.getMonth() &&
          date.getFullYear() === disabledDate.getFullYear()
      );
    }

    return false;
  }

  // Get week number for a date
  getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  }

  // Handle hour change in time picker
  onHourChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let hours = parseInt(input.value, 10);

    if (isNaN(hours)) return;

    if (!this.use24Hour) {
      hours = this.convert12HourTo24Hour(hours);
    }

    this.updateDateWithHours(hours);
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  private convert12HourTo24Hour(hours: number): number {
    this.ensureSelectedDateIsDate();
    const isPM =
      this.selectedDate && this.selectedDate instanceof Date
        ? this.selectedDate.getHours() >= 12
        : false;
    if (hours === 12) {
      return isPM ? 12 : 0;
    }
    return isPM ? hours + 12 : hours;
  }

  private updateDateWithHours(hours: number): void {
    if (!this.selectedDate) {
      const now = new Date();
      this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, 0, 0);
      return;
    }

    this.ensureSelectedDateIsDate();
    if (this.selectedDate instanceof Date) {
      this.selectedDate.setHours(hours);
    } else {
      // If conversion failed, create a new date
      const now = new Date();
      this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, 0, 0);
    }
  }

  // Handle minute change in time picker
  onMinuteChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const minutes = parseInt(input.value, 10);

    if (isNaN(minutes)) return;

    if (!this.selectedDate) {
      const now = new Date();
      this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, minutes, 0);
    } else {
      this.ensureSelectedDateIsDate();
      if (this.selectedDate instanceof Date) {
        this.selectedDate.setMinutes(minutes);
      } else {
        // If conversion failed, create a new date
        const now = new Date();
        this.selectedDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0,
          minutes,
          0
        );
      }
    }

    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Handle AM/PM change in time picker
  onAmPmChange(event: Event): void {
    if (!this.selectedDate) return;

    this.ensureSelectedDateIsDate();
    if (!(this.selectedDate instanceof Date)) {
      this.selectedDate = new Date();
    }

    const select = event.target as HTMLSelectElement;
    const isPM = select.value === 'PM';
    const hours = this.selectedDate.getHours();
    const is24PM = hours >= 12;

    if (isPM && !is24PM) {
      this.selectedDate.setHours(hours + 12);
    } else if (!isPM && is24PM) {
      this.selectedDate.setHours(hours - 12);
    }

    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  onInputFocus(): void {
    if (this.allowInput) {
      this.isInputFocused = true;
    }
  }

  onInputClick(event: MouseEvent): void {
    if (!this.allowInput || this.disabled || this.isLoading) {
      return;
    }
    event.stopPropagation();
    const input = this.inputElement?.nativeElement as HTMLInputElement | undefined;
    input?.focus();
  }

  onInputMouseDown(event: MouseEvent): void {
    if (!this.allowInput || this.disabled || this.isLoading) {
      return;
    }
    event.stopPropagation();
  }

  onInputBlur(event: Event): void {
    if (!this.allowInput) {
      this.onTouched();
      return;
    }

    this.isInputFocused = false;
    const input = event.target as HTMLInputElement;
    let value = input.value.trim();

    if (this.usesUsDateMask()) {
      const digits = this.extractDateDigits(value);
      value = digits.length > 0 ? this.formatDigitsAsMmDdYyyy(digits) : '';
      input.value = value;
      this.inputValue = value;
    }

    if (value === '') {
      if (this.commitSelectedDateOnBlur()) {
        this.onTouched();
        return;
      }
      this.clearDate();
      this.onTouched();
      return;
    }

    const parsedDate = this.parseTypedDate(value);
    if (parsedDate && !this.isDisabled(parsedDate)) {
      this.applyParsedDate(parsedDate);
    } else if (this.commitSelectedDateOnBlur()) {
      // Typed text invalid but a calendar-selected date is still valid — keep it.
    } else {
      this.selectedDate = null;
      this.inputValue = value;
      this.applyInputDisplayValue();
      this.onChange(null);
      this.dateChange.emit(null);
    }

    this.onTouched();
  }

  private commitSelectedDateOnBlur(): boolean {
    this.ensureSelectedDateIsDate();
    if (!this.selectedDate || !(this.selectedDate instanceof Date)) {
      return false;
    }
    if (this.isDisabled(this.selectedDate)) {
      return false;
    }
    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
    return true;
  }

  onInputKeyDown(event: KeyboardEvent): void {
    if (!this.allowInput || !this.usesUsDateMask() || this.disabled || this.isLoading) {
      return;
    }

    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];
    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  // Handle direct input change while typing (MM/DD/YYYY mask when applicable)
  onInputChange(event: Event): void {
    if (!this.allowInput) {
      return;
    }

    const input = event.target as HTMLInputElement;
    this.isInputFocused = true;

    let displayValue = input.value;
    if (this.usesUsDateMask()) {
      const digits = this.extractDateDigits(displayValue);
      displayValue = this.formatDigitsAsMmDdYyyy(digits);
    }

    if (input.value !== displayValue) {
      input.value = displayValue;
    }
    this.inputValue = displayValue;

    if (displayValue === '') {
      this.selectedDate = null;
      this.onChange(null);
      this.dateChange.emit(null);
      return;
    }

    try {
      const parsedDate = this.parseTypedDate(displayValue);
      if (parsedDate && !this.isDisabled(parsedDate)) {
        this.selectedDate = parsedDate;
        this.currentMonth = parsedDate.getMonth();
        this.currentYear = parsedDate.getFullYear();
        this.generateCalendarDays();
        this.inputValue = this.formatDateTime(parsedDate);
        this.applyInputDisplayValue();
        this.onChange(this.selectedDate);
        this.dateChange.emit(this.selectedDate);
      } else if (this.usesUsDateMask()) {
        this.selectedDate = null;
        this.onChange(null);
        this.dateChange.emit(null);
      }
    } catch {
      // Keep masked partial input visible
    }
  }

  usesUsDateMask(): boolean {
    if (!this.allowInput) {
      return false;
    }
    const format = (this.format || '').toLowerCase();
    return format === 'mm/dd/yyyy' || format.includes('mm/dd');
  }

  private extractDateDigits(value: string): string {
    return value.replace(/\D/g, '').slice(0, 8);
  }

  private formatDigitsAsMmDdYyyy(digits: string): string {
    const d = digits.slice(0, 8);
    if (d.length <= 2) {
      return d;
    }
    if (d.length <= 4) {
      return `${d.slice(0, 2)}/${d.slice(2)}`;
    }
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  }

  private parseTypedDate(value: string): Date | null {
    if (this.usesUsDateMask()) {
      const digits = this.extractDateDigits(value);
      if (digits.length < 8) {
        return null;
      }
      return this.parseMaskedUsDateDigits(digits);
    }
    return this.parseInputDate(value);
  }

  private parseMaskedUsDateDigits(digits: string): Date | null {
    if (digits.length !== 8) {
      return null;
    }

    const month = parseInt(digits.slice(0, 2), 10);
    const day = parseInt(digits.slice(2, 4), 10);
    const year = parseInt(digits.slice(4, 8), 10);

    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000 || year > 9999) {
      return null;
    }

    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private parseInputDate(value: string): Date | null {
    const parts = value.split(/[-/\s,]+/).filter((p) => p.length > 0);
    if (parts.length < 3) {
      return null;
    }

    const p0 = parseInt(parts[0], 10);
    const p1 = parseInt(parts[1], 10);
    const p2 = parseInt(parts[2], 10);

    if (isNaN(p0) || isNaN(p1) || isNaN(p2)) {
      return null;
    }

    let year: number;
    let month: number;
    let day: number;

    // Detect format: YYYY-MM-DD (year first, 4 digits) vs MM-DD-YYYY / MM/DD/YYYY (month first)
    const isYearFirst = parts[0].length >= 4 && p0 >= 1000 && p0 <= 9999;
    if (isYearFirst) {
      year = p0;
      month = p1 - 1;
      day = p2;
    } else if (parts[2].length >= 4 && p2 >= 1000 && p2 <= 9999) {
      // MM-DD-YYYY or MM/DD/YYYY
      month = p0 - 1;
      day = p1;
      year = p2;
    } else {
      // Fallback: assume YYYY-MM-DD
      year = p0;
      month = p1 - 1;
      day = p2;
    }

    const newDate = new Date(year, month, day);
    if (isNaN(newDate.getTime())) {
      return null;
    }
    this.applyTimeToDate(newDate, parts);
    return newDate;
  }

  private applyTimeToDate(date: Date, parts: string[]): void {
    if (!this.enableTime || parts.length < 5) {
      return;
    }

    const hours = parseInt(parts[3], 10);
    const minutes = parseInt(parts[4], 10);
    const seconds = parts.length >= 6 ? parseInt(parts[5], 10) : 0;

    if (!isNaN(hours) && !isNaN(minutes) && !isNaN(seconds)) {
      date.setHours(hours, minutes, seconds);
    }
  }

  private applyParsedDate(newDate: Date): void {
    this.selectedDate = newDate;
    this.currentMonth = newDate.getMonth();
    this.currentYear = newDate.getFullYear();
    this.generateCalendarDays();
    this.updateInputValue(); // Keep input display in sync when user types a date manually
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Helper to pad numbers with leading zero
  padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  // Helper to convert 24h to 12h format
  get12Hour(hour: number): number {
    if (hour === 0) return 12;
    if (hour > 12) return hour - 12;
    return hour;
  }

  // Get hours for display in the time picker
  getDisplayHours(): string {
    if (!this.selectedDate) return this.use24Hour ? '00' : '12';

    const hours = this.selectedDate.getHours();
    return this.use24Hour ? this.padZero(hours) : this.padZero(this.get12Hour(hours));
  }

  // ControlValueAccessor methods
  writeValue(value: Date | null | string): void {
    if (this.allowInput && this.isInputFocused) {
      return;
    }

    let dateValue: Date | null = null;
    if (value instanceof Date) {
      dateValue = value;
    } else if (typeof value === 'string') {
      dateValue = DateUtils.coerceToLocalDate(value);
    } else if (typeof value === 'number') {
      dateValue = DateUtils.coerceToLocalDate(value);
    }

    if (dateValue !== null && dateValue !== undefined) {
      this.selectedDate = dateValue;
      this.currentMonth = this.selectedDate.getMonth();
      this.currentYear = this.selectedDate.getFullYear();
      this.generateCalendarDays();
      this.updateInputValue();

      if (this.isInitialLoad) {
        this.initialValue = dateValue;
      }
    } else {
      if (this.isInitialLoad) {
        this.initialValue = null;
      }

      this.selectedDate = null;
      this.inputValue = '';
      this.applyInputDisplayValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Add this method to close the calendar
  closeCalendar(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isOpen = false;
  }

  // Update the onTimeInputChange method to immediately enforce length and value limits
  onTimeInputChange(event: Event, field: TimeField): void {
    const input = event.target as HTMLInputElement;

    // First, truncate to maximum 2 digits
    if (input.value.length > 2) {
      input.value = input.value.slice(0, 2);
    }

    let value = parseInt(input.value, 10);

    if (isNaN(value)) {
      return;
    }

    // Apply range limits immediately during input
    let maxValue = 59;
    if (field === 'hour') {
      maxValue = this.use24Hour ? 23 : 12;
      const minValue = this.use24Hour ? 0 : 1;

      if (value < minValue) value = minValue;
      if (value > maxValue) {
        input.value = maxValue.toString();
        value = maxValue;
      }
    } else if (value > maxValue) {
      input.value = maxValue.toString();
      value = maxValue;
    }

    // Update the field with the valid value
    this.updateTimeField(value, field);
  }

  // Validate time input on blur
  validateTimeInput(event: Event, field: TimeField): void {
    const input = event.target as HTMLInputElement;
    let value = parseInt(input.value, 10);

    if (isNaN(value)) {
      value = 0;
    }

    // Apply range limits based on field
    switch (field) {
      case 'hour':
        if (this.use24Hour) {
          value = Math.min(Math.max(value, 0), 23);
        } else {
          value = Math.min(Math.max(value, 1), 12);
        }
        break;
      case 'minute':
      case 'second':
        value = Math.min(Math.max(value, 0), 59);
        break;
    }

    // Update the field with the valid value
    this.updateTimeField(value, field);

    // Refresh display
    if (field === 'hour') {
      input.value = this.getDisplayHours();
    } else {
      input.value = this.padZero(value);
    }
  }

  // Update time field with new value
  updateTimeField(value: number, field: TimeField): void {
    if (!this.selectedDate) {
      this.selectedDate = new Date();
    }

    switch (field) {
      case 'hour':
        // Handle 12/24 hour conversion
        if (!this.use24Hour) {
          // In 12-hour mode, convert 12 to 0 for AM, keep 12 for PM
          const isPM = this.selectedDate.getHours() >= 12;
          if (value === 12) {
            value = isPM ? 12 : 0;
          } else if (isPM) {
            value += 12;
          }
        }
        this.selectedDate.setHours(value);
        break;
      case 'minute':
        this.selectedDate.setMinutes(value);
        break;
      case 'second':
        this.selectedDate.setSeconds(value);
        break;
    }

    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Set AM or PM
  setAmPm(period: 'AM' | 'PM'): void {
    if (!this.selectedDate) {
      this.selectedDate = new Date();
    } else {
      this.ensureSelectedDateIsDate();
      if (!(this.selectedDate instanceof Date)) {
        this.selectedDate = new Date();
      }
    }

    const hours = this.selectedDate.getHours();
    const isPM = hours >= 12;

    if (period === 'AM' && isPM) {
      this.selectedDate.setHours(hours - 12);
    } else if (period === 'PM' && !isPM) {
      this.selectedDate.setHours(hours + 12);
    }

    this.updateInputValue();
    this.onChange(this.selectedDate);
    this.dateChange.emit(this.selectedDate);
  }

  // Prevent entering non-numeric characters in time inputs
  preventInvalidTimeChars(event: KeyboardEvent): void {
    // Prevent 'e', '+', '-' in number inputs
    if (['e', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  // Add this method to check if a navigation button is disabled
  isDisabledNavigationButton(direction: 'prev' | 'next'): boolean {
    if (!this.minDate && !this.maxDate) return false;

    const testDate = new Date(this.currentYear, this.currentMonth);

    if (direction === 'prev') {
      // Move to previous month for testing
      testDate.setMonth(testDate.getMonth() - 1);
      // Check if the 1st of previous month is before minDate
      return this.minDate
        ? testDate < new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1)
        : false;
    } else {
      // Move to next month for testing
      testDate.setMonth(testDate.getMonth() + 1);
      // Check if the 1st of next month is after maxDate
      return this.maxDate
        ? testDate > new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1)
        : false;
    }
  }

  // Check if a date is the start date
  isStartDate(date: Date): boolean {
    if (!this.rangeMode || !this.startDate) return false;

    return (
      date.getDate() === this.startDate.getDate() &&
      date.getMonth() === this.startDate.getMonth() &&
      date.getFullYear() === this.startDate.getFullYear()
    );
  }

  // Check if a date is the end date
  isEndDate(date: Date): boolean {
    if (!this.rangeMode || !this.endDate) return false;

    return (
      date.getDate() === this.endDate.getDate() &&
      date.getMonth() === this.endDate.getMonth() &&
      date.getFullYear() === this.endDate.getFullYear()
    );
  }

  // Check if a date is in the range
  isInRange(date: Date): boolean {
    if (!this.rangeMode || !this.startDate || !this.endDate) return false;

    return date > this.startDate && date < this.endDate;
  }

  // Handle range selection
  handleRangeSelection(date: Date): void {
    if (this.isDisabled(date) || !this.isInCurrentMonth(date)) return;

    if (this.selectionStep === 'start' || (this.startDate && this.endDate)) {
      // Start new selection
      this.startDate = new Date(date);
      this.endDate = null;
      this.selectionStep = 'end';
      this.updateInputValue();
    } else {
      // Complete the selection
      if (date < this.startDate!) {
        // If end date is before start date, swap them
        this.endDate = this.startDate;
        this.startDate = new Date(date);
      } else {
        this.endDate = new Date(date);
      }
      this.selectionStep = 'start';
      this.updateInputValue();
    }

    this.rangeChange.emit({
      start: this.startDate,
      end: this.endDate,
    });
  }

  // Format date using DatePipe (normalize MM/DD/YYYY → MM/dd/yyyy so DD is day-of-month)
  formatDate(date: Date): string {
    if (!date) return '';
    if (this.usesUsDateMask()) {
      return DateUtils.formatUsDisplay(date);
    }
    const pipeFormat = DateUtils.normalizeAngularDateFormat(this.format);
    return this.datePipe.transform(date, pipeFormat) || '';
  }

  // Format date and time together
  formatDateTime(date: Date): string {
    if (!this.enableTime) {
      return this.formatDate(date);
    }

    const dateStr = this.formatDate(date);
    const timeStr = this.datePipe.transform(date, this.timeFormat) || '';

    return `${dateStr} ${timeStr}`;
  }

  // Get start hours for range mode
  getStartHours(): string {
    if (!this.startDate) return this.use24Hour ? '00' : '12';

    const hours = this.startDate.getHours();
    return this.use24Hour ? this.padZero(hours) : this.padZero(this.get12Hour(hours));
  }

  // Get end hours for range mode
  getEndHours(): string {
    if (!this.endDate) return this.use24Hour ? '00' : '12';

    const hours = this.endDate.getHours();
    return this.use24Hour ? this.padZero(hours) : this.padZero(this.get12Hour(hours));
  }

  // Handle range time input change
  onRangeTimeInputChange(event: Event, field: 'hour' | 'minute', type: 'start' | 'end'): void {
    const input = event.target as HTMLInputElement;

    // First, truncate to maximum 2 digits
    if (input.value.length > 2) {
      input.value = input.value.slice(0, 2);
    }

    let value = parseInt(input.value, 10);

    if (isNaN(value)) {
      return;
    }

    // Apply range limits immediately during input
    let maxValue = 59;
    if (field === 'hour') {
      maxValue = this.use24Hour ? 23 : 12;
      const minValue = this.use24Hour ? 0 : 1;

      if (value < minValue) value = minValue;
      if (value > maxValue) {
        input.value = maxValue.toString();
        value = maxValue;
      }
    } else if (value > maxValue) {
      input.value = maxValue.toString();
      value = maxValue;
    }

    // Update the field with the valid value
    this.updateRangeTimeField(value, field, type);
  }

  // Validate range time input
  validateRangeTimeInput(event: Event, field: 'hour' | 'minute', type: 'start' | 'end'): void {
    const input = event.target as HTMLInputElement;
    let value = parseInt(input.value, 10);

    if (isNaN(value)) {
      value = 0;
    }

    // Apply range limits based on field
    switch (field) {
      case 'hour':
        if (this.use24Hour) {
          value = Math.min(Math.max(value, 0), 23);
        } else {
          value = Math.min(Math.max(value, 1), 12);
        }
        break;
      case 'minute':
        value = Math.min(Math.max(value, 0), 59);
        break;
    }

    // Update the field with the valid value
    this.updateRangeTimeField(value, field, type);

    // Refresh display
    if (field === 'hour') {
      input.value = type === 'start' ? this.getStartHours() : this.getEndHours();
    } else {
      input.value = this.padZero(value);
    }
  }

  // Update time field for range
  updateRangeTimeField(value: number, field: 'hour' | 'minute', type: 'start' | 'end'): void {
    const dateToUpdate = this.getOrCreateRangeDate(type);

    if (field === 'hour') {
      const convertedHour = this.convertHourForRange(value, dateToUpdate);
      dateToUpdate.setHours(convertedHour);
    } else {
      dateToUpdate.setMinutes(value);
    }

    this.updateInputValue();
    this.rangeChange.emit({
      start: this.startDate,
      end: this.endDate,
    });
  }

  private getOrCreateRangeDate(type: 'start' | 'end'): Date {
    let dateToUpdate = type === 'start' ? this.startDate : this.endDate;

    if (!dateToUpdate) {
      dateToUpdate = new Date();
      if (type === 'start') {
        this.startDate = dateToUpdate;
      } else {
        this.endDate = dateToUpdate;
      }
    }

    return dateToUpdate;
  }

  private convertHourForRange(value: number, dateToUpdate: Date): number {
    if (this.use24Hour) {
      return value;
    }

    // In 12-hour mode, convert 12 to 0 for AM, keep 12 for PM
    const isPM = dateToUpdate.getHours() >= 12;
    if (value === 12) {
      return isPM ? 12 : 0;
    }
    return isPM ? value + 12 : value;
  }

  // Set AM/PM for range time
  setRangeAmPm(period: 'AM' | 'PM', type: 'start' | 'end'): void {
    let dateToUpdate = type === 'start' ? this.startDate : this.endDate;

    if (!dateToUpdate) {
      dateToUpdate = new Date();
      if (type === 'start') {
        this.startDate = dateToUpdate;
      } else {
        this.endDate = dateToUpdate;
      }
    }

    const hours = dateToUpdate.getHours();
    const isPM = hours >= 12;

    if (period === 'AM' && isPM) {
      dateToUpdate.setHours(hours - 12);
    } else if (period === 'PM' && !isPM) {
      dateToUpdate.setHours(hours + 12);
    }

    this.updateInputValue();
    this.rangeChange.emit({
      start: this.startDate,
      end: this.endDate,
    });
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    if (
      this.isOpen &&
      !this.inline &&
      !this.datePickerContainer.nativeElement.contains(event.target)
    ) {
      this.isOpen = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) {
      // Re-calculate position when window is resized
      this.determineCalendarPosition();
    }
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.monthDropdownOpen = false;
    this.yearDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isOpen && !this.datePickerContainer.nativeElement.contains(event.target as Node)) {
      this.closeCalendar(event);
    }
  }

  // Clear range date selection
  clearRangeDate(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.startDate = null;
    this.endDate = null;
    this.selectionStep = 'start';
    this.inputValue = '';
    this.rangeChange.emit({
      start: null,
      end: null,
    });
    this.startRipple();
  }
}
